/**
 * The client-side half of the BGG licence contract.
 *
 * `src/server/bgg.ts` owns `sizedImageUrl` and `ATTRIBUTION`, and this file
 * deliberately restates both rather than importing them. That module's first
 * statement is `import { db }` — pulling it into the bundle would drag
 * `bun:sqlite` into a browser build. The duplication is the cheaper of the two
 * evils, and it is duplication of a *contract*, not of logic that will drift:
 * the rules below are fixed by BGG's terms, not by our implementation.
 *
 * Both rules are licence obligations, not styling choices:
 *
 *  - BGG data must never be modified, box art included. So the bytes are never
 *    touched: no crop, no filter, no watermark. Resizing happens only by asking
 *    BGG's own Thumbor CDN for a different rendition through URL parameters,
 *    which is the sanctioned path. Rendering uses `object-fit: contain` for the
 *    same reason — `cover` would crop the art in the viewport.
 *  - Attribution is mandatory: BoardGameGeek is named in text, linked, wherever
 *    their data appears. Deliberately no "Powered by BGG" logo — that mark comes
 *    with being a licensed user of their API, and this build is not one. Without
 *    a `BGG_TOKEN` the API is never called at all, and the catalog that ships
 *    instead is a community snapshot of BGG's public rankings. Rendering their
 *    mark would claim a relationship that does not exist.
 */

/** Mirrors `ATTRIBUTION` in `src/server/bgg.ts`. Keep the two in step. */
export const ATTRIBUTION = {
  text: "Game data from BoardGameGeek",
  linkText: "boardgamegeek.com",
  href: "https://boardgamegeek.com",
} as const;

/**
 * Ask BGG's CDN for a smaller rendition of a BGG-hosted image.
 *
 * Mirrors `sizedImageUrl` in `src/server/bgg.ts`. Non-geekdo URLs come back
 * untouched: a manually entered game's image is not ours to rewrite either.
 */
export function sizedImageUrl(url: string | null, width: number, height: number): string | null {
  if (!url) return null;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  if (!/(^|\.)geekdo-images\.com$/.test(parsed.hostname)) return url;
  parsed.searchParams.set("fit", "contain");
  parsed.searchParams.set("w", String(width));
  parsed.searchParams.set("h", String(height));
  return parsed.toString();
}

/**
 * The real pixel width behind a URL, when the URL says so.
 *
 * BGG's CDN is Thumbor and encodes the rendition in the path — `/fit-in/64x64/`
 * is a genuinely 64px image, not a 64px request against something larger. That
 * number is worth reading, because the path is signed: asking for a bigger crop
 * answers HTTP 400, and the query parameters `sizedImageUrl` adds are ignored
 * outright (verified — identical bytes with and without). So this is not a hint,
 * it is the ceiling, and rendering above it is guaranteed blur.
 *
 * The bundled index carries only the 64px `__micro` rendition, which is why this
 * matters at all: without the cap, a shelf tile stretched it 2.75x. Full-size
 * URLs from `getThings` carry no `fit-in` segment and return null here, meaning
 * "no known ceiling" — so the same components render large the moment a token
 * fills `image_url`, with no other change.
 */
export function intrinsicWidth(url: string | null): number | null {
  if (!url) return null;
  const match = /\/fit-in\/(\d+)x/.exec(url);
  if (!match) return null;
  const width = Number(match[1]);
  return width > 0 ? width : null;
}
